import { createReducer } from '@reduxjs/toolkit';

import {
  aiChat,
  approvedJob,
  candidateSelectionGraphData,
  candidateSelectionMilesGraphData,
  deleteJob,
  duplicateJobData,
  exportCandidates,
  generateCandidate,
  generateJob,
  getCandidatesList,
  getChats,
  getJobDetail,
  getJobNPDetail,
  getJobsList,
  jobAIChat,
  resetCandidates,
  updateJob,
  uploadDocuments
} from './actions';
import { NUMBERS } from '../../../resources/number-constants';
import { removeDuplicateRecords } from '../../../utility/helpers';

const initialState: any = {
  data: null,
  aiChats: {
    results: [],
    currentPage: 0,
    total: 1,
    totalEntries: 0,
    responseStatus: true,
  },
  candidates: {
    results: [],
    currentPage: 1,
    itemsPerPage: 0,
    totalItems: 0,
    filterCount: null,
    generatedDate: '',
    hasNextPage: false,
    tier_limitation: null,
    currentJobId: null, // Track current job to ignore stale responses
  },
  jobs: {
    results: [],
    currentPage: 1,
    itemsPerPage: 0,
    totalItems: 0,
    generatedDate: '',
    hasNextPage: false,
  }
};

const dashboardReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(uploadDocuments.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(getChats.fulfilled, (state, { payload }) => {
      const { queryParams, total, data, responseStatus, usersCount }: any = payload;
      state.aiChats.results = data;
      state.aiChats.results = removeDuplicateRecords(state.aiChats.results);
      state.aiChats.total = Math.ceil(usersCount / NUMBERS.TEN);
      state.aiChats.currentPage = queryParams.page;
      state.aiChats.totalEntries = total;
      state.aiChats.responseStatus = responseStatus;
    })
    .addCase(generateJob.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(updateJob.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(jobAIChat.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(approvedJob.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(candidateSelectionGraphData.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(candidateSelectionMilesGraphData.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(generateCandidate.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(getCandidatesList.fulfilled, (state, { payload }) => {
      const { queryParams, data, itemsPerPage, totalItems, filterCount, generatedDate, nextPage, tier_limitation }: any = payload;

      // CRITICAL: Ignore stale responses from different jobs
      const responseJobId = queryParams.job_id;

      // If job_id changed, ignore stale responses
      if (state.candidates.currentJobId && responseJobId !== state.candidates.currentJobId) {
        console.info('Ignoring stale candidates response for job:', responseJobId, 'Current job:', state.candidates.currentJobId);
        return;
      }

      // Set current job ID if not set
      if (!state.candidates.currentJobId) {
        state.candidates.currentJobId = responseJobId;
      }

      // For pagination: always replace results with the current page data
      state.candidates.results = data;

      state.candidates.results = removeDuplicateRecords(state.candidates.results);
      state.candidates.currentPage = queryParams.page;
      state.candidates.itemsPerPage = itemsPerPage;
      state.candidates.totalItems = totalItems;
      if (filterCount !== undefined) {
        state.candidates.filterCount = filterCount;
      }
      state.candidates.generatedDate = generatedDate;
      state.candidates.hasNextPage = nextPage !== null;
      // Persist tier limitation info from API so UI can display counts correctly
      if (tier_limitation !== undefined) {
        state.candidates.tier_limitation = tier_limitation;
      }
    })
    .addCase(getJobsList.fulfilled, (state, { payload }) => {
      const { queryParams, data, itemsPerPage, totalItems, generatedDate, nextPage }: any = payload;

      if (queryParams.page === NUMBERS.ONE) {
        state.jobs.results = data;
      } else {
        state.jobs.results = [
          ...state.jobs.results,
          ...data,
        ];
      }
      state.jobs.results = removeDuplicateRecords(state.jobs.results);
      state.jobs.currentPage = queryParams.page;
      state.jobs.itemsPerPage = itemsPerPage;
      state.jobs.totalItems = totalItems;
      state.jobs.generatedDate = generatedDate;
      state.jobs.hasNextPage = nextPage !== null;
    })
    .addCase(deleteJob.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(duplicateJobData.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(exportCandidates.fulfilled, (state, { payload }) => {
      state.data = payload;
    })
    .addCase(aiChat.fulfilled, (state, { payload }) => {
      state.data = payload;
    })
    .addCase(getJobDetail.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(getJobNPDetail.fulfilled, (state, { payload }) => {
      state.data = payload?.data;
    })
    .addCase(resetCandidates, (state, action) => {
      state.candidates = {
        results: [],
        currentPage: 1,
        itemsPerPage: 0,
        totalItems: 0,
        filterCount: null,
        generatedDate: '',
        hasNextPage: false,
        tier_limitation: null,
        currentJobId: action.payload || null, // Set to new jobId if provided
      };
    });
});

export default dashboardReducer;
